// Pure helpers for the Predict → Coverage page (STRY0038969, redesigned in
// STRY0039118): the summary cards, the customer's vendors grouped by how
// Predict covers them, a vendor's managed products and the open-source
// projects inside them (SBOM). The page renders; this decides.
//
// Data: GET /predict/scope's `entries` (one per target, each with the Prevent
// products it came from in `from`) and `coverage` (each classified vendor's
// class, from Predict's coverage map in ODD, STRY0039045) with `reasons` (why
// Predict doesn't cover a `none` vendor) and `coverageAvailable`, the shell's vendor list, and a vendor's managed products
// (GET /managedProducts?vendorId=).

import { productDisplayName } from '../bz/productDisplayName';
import { sourceCodes, sourceWords } from './predictSbomSources';

// An entry none of whose rows name a vendor (a row added by hand, such as a
// demo row, which the API keeps) belongs to this pseudo-vendor.
export const OTHER_VENDOR = '_other';
const OTHER_NAME = 'Other';

// How Predict covers a vendor, in the order the list shows them. Not the
// 7-day status: a quiet week with no new bugs doesn't move a vendor.
export const GROUPS = [
  { id: 'tracker', label: 'Watched through trackers and OSS', tone: 'ok' },
  { id: 'product', label: 'Watched per product or vendor', tone: 'info' },
  { id: 'waiting', label: 'Waiting for a run', tone: 'warn' },
  { id: 'none', label: 'Nothing to score', tone: 'neutral' },
  { id: 'unset', label: 'Not set up for Predict', tone: 'neutral' }
];

export const groupOf = id => GROUPS.find(group => group.id === id) || GROUPS[GROUPS.length - 1];

const NONE_REASON = 'Service health and configuration events, not filed bugs.';
const WAITING_REASON = "Fills in after this vendor's next run in Prevent.";
const UNSET_REASON = 'Not set up for Predict yet.';
const COVERAGE_UNAVAILABLE = "Predict's coverage details are unavailable right now.";

const distinct = values => [...new Set(values)];
const byName = (a, b) => (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' });
const fromOf = entry => (Array.isArray(entry.from) ? entry.from : []);
const sbomItems = entry => fromOf(entry).filter(source => source.origin === 'sbom');
// A product's devices on a release its vendor publishes no SBOM for (STRY0039119): shown on the
// product, never counted as something Predict watches.
const NO_LIST = 'noList';

// SBOM rows' sources in words ("F5 SBOM", "no SBOM"), each once: the stored codes, worded for the
// vendor the rows belong to (predictSbomSources).
const sourceLabels = (items, vendorName) =>
  distinct(items.flatMap(item => sourceCodes(item.componentSource)).map(code => sourceWords(code, vendorName)));

/**
 * The vendors an entry is listed under: every vendor its rows came from, so
 * an open-source project inside both Red Hat and Cisco products is under both
 * (the API's own `vendorId` is null for it), else "Other".
 */
export const vendorKeys = entry => {
  const ids = distinct([entry.vendorId, ...fromOf(entry).map(source => source.vendorId)].filter(Boolean));
  return ids.length ? ids : [OTHER_VENDOR];
};

// Prevent vendors that are switched on and configured (GET /vendors'
// `configured`; a vendor without the flag counts as not configured).
const switchedOn = vendors =>
  (Array.isArray(vendors) ? vendors : []).filter(v => v?.type === 'vendor' && v.active && v.configured);

/**
 * One row per vendor the customer has switched on, plus any vendor the scope
 * names that the list doesn't (and "Other" for entries with no vendor):
 * {id, name, group, count, entries, coverage, reason}. `count` is how many
 * entries Predict has for it; `reason` explains an empty vendor. `about` is the
 * rest of GET /predict/scope: a `none` vendor's reason is its `reasons` entry,
 * and with `coverageAvailable` false an empty vendor's reason says the details
 * can't be read rather than guessing.
 */
export const vendorRows = (vendors, entries, coverage, vendorNameOf, about = {}) => {
  const byVendor = new Map();
  entries.forEach(entry =>
    vendorKeys(entry).forEach(key => {
      if (!byVendor.has(key)) byVendor.set(key, []);
      byVendor.get(key).push(entry);
    })
  );
  const ids = distinct([...switchedOn(vendors).map(v => v.id), ...byVendor.keys()]);
  return ids
    .map(id => {
      const mine = byVendor.get(id) || [];
      const watchable = mine.filter(entry => entry.kind !== NO_LIST);
      const klass = coverage?.[id];
      let group = 'unset';
      if (watchable.some(entry => entry.kind === 'tracker' || entry.kind === 'project')) group = 'tracker';
      else if (watchable.length) group = 'product';
      else if (klass === 'none') group = 'none';
      else if (klass) group = 'waiting';
      let reason = '';
      if (about.coverageAvailable === false && !watchable.length) reason = COVERAGE_UNAVAILABLE;
      else if (group === 'none') reason = about.reasons?.[id] || NONE_REASON;
      else if (group === 'waiting') reason = WAITING_REASON;
      else if (group === 'unset') reason = UNSET_REASON;
      return {
        id,
        name: id === OTHER_VENDOR ? OTHER_NAME : vendorNameOf(id),
        group,
        count: watchable.length,
        entries: mine,
        coverage: klass || null,
        reason
      };
    })
    .sort(
      (a, b) =>
        GROUPS.findIndex(g => g.id === a.group) - GROUPS.findIndex(g => g.id === b.group) || byName(a.name, b.name)
    );
};

// The vendor shown first: the first one Predict scored this week, else the first in the list.
export const defaultVendor = rows =>
  (rows.find(row => row.entries.some(entry => entry.status === 'scored')) || rows[0] || {}).id || null;

const listNames = names => {
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
};

/**
 * The four summary cards: vendors switched on, scored this week, OSS (the
 * open-source projects inside products, and whose products), managed products
 * covered. Each is {label, value, meta}.
 */
export const summaryCards = (rows, entries, vendorNameOf) => {
  const real = rows.filter(row => row.id !== OTHER_VENDOR);
  const covered = real.filter(row => row.count > 0);
  const scored = real.filter(row => row.entries.some(entry => entry.status === 'scored'));
  const projects = entries.filter(entry => entry.kind === 'project');
  const projectVendors = distinct(projects.flatMap(entry => sbomItems(entry).map(s => s.vendorId)).filter(Boolean))
    .map(vendorNameOf)
    .sort(byName);
  const products = new Set(
    entries.flatMap(entry =>
      fromOf(entry)
        .filter(s => s.origin === 'prevent' && s.managedProductId != null)
        .map(s => `${s.vendorId}:${s.managedProductId}`)
    )
  );
  return [
    { label: 'Vendors switched on', value: String(real.length), meta: `${covered.length} covered by Predict` },
    {
      label: 'Scored this week',
      value: `${scored.length} ${scored.length === 1 ? 'vendor' : 'vendors'}`,
      meta: scored.length ? listNames(scored.map(row => row.name)) : 'no new bugs this week'
    },
    {
      label: 'OSS',
      value: String(projects.length),
      meta: projectVendors.length
        ? `inside products from ${projectVendors.length} ${
            projectVendors.length === 1 ? 'vendor' : 'vendors'
          }: ${listNames(projectVendors)}`
        : 'none found in your products yet'
    },
    { label: 'Managed products covered', value: String(products.size), meta: 'from your Prevent inventory' }
  ];
};

/** One line on what Predict watches for a vendor. */
export const vendorSummary = row => {
  if (!row) return '';
  if (row.group === 'none' || row.group === 'waiting' || row.group === 'unset') return row.reason;
  const tracker = row.entries.find(entry => entry.kind === 'tracker');
  const hasProjects = row.entries.some(entry => entry.kind === 'project');
  let watches = 'Predict watches this vendor’s bugs.';
  if (tracker) watches = `Predict watches ${tracker.name} (${tracker.detail}).`;
  else if (row.coverage === 'product') watches = 'Predict watches this vendor’s bugs for each of your products.';
  else if (row.coverage === 'vendor') watches = 'Predict watches all of this vendor’s bugs.';
  return hasProjects ? `${watches} It also watches the OSS inside your ${row.name} products.` : watches;
};

/**
 * A vendor's managed products as the table shows them, by name. `products`
 * is GET /managedProducts for the vendor; a product Prevent no longer sees
 * (`missingSince`) is left out. Each row: {id, name, versions, hosts,
 * sources, projects, covered}; `covered` is how Predict covers it, or why not.
 */
export const productRows = (products, row) => {
  const entries = row ? row.entries : [];
  return (Array.isArray(products) ? products : [])
    .filter(product => product && !product.missingSince)
    .map(product => {
      const items = entries.flatMap(entry =>
        fromOf(entry)
          .filter(s => s.managedProductId === product.id)
          .map(s => ({ ...s, entry }))
      );
      const sbom = items.filter(item => item.origin === 'sbom');
      const prevent = items.filter(item => item.origin === 'prevent');
      const first = sbom[0];
      let covered = 'Not covered';
      if (prevent.some(item => item.entry.kind === 'tracker')) covered = 'Bug tracker';
      else if (prevent.some(item => item.entry.kind === 'product')) covered = 'Product bugs';
      else if (prevent.some(item => item.entry.kind === 'vendor')) covered = 'All vendor bugs';
      else if (row?.coverage === 'product') covered = 'No ODD product ID yet';
      return {
        id: product.id,
        name: productDisplayName(row?.id, product.name),
        versions: first?.originVersions || '',
        hosts: first?.originHosts ?? null,
        sources: sourceLabels(sbom, row?.name),
        projects: sbom.filter(item => item.entry.kind === 'project').length,
        covered
      };
    })
    .sort((a, b) => byName(a.name, b.name));
};

/**
 * The open-source projects inside a vendor's products: {target, name, detail,
 * status, hosts, sources}. `hosts` adds the project's host counts across the
 * vendor's products; `sources` are its sources in words, for that vendor.
 */
export const projectsOf = row =>
  (row ? row.entries : [])
    .filter(entry => entry.kind === 'project')
    .map(entry => {
      const items = sbomItems(entry).filter(s => s.vendorId === row.id);
      return {
        target: entry.target,
        name: entry.name,
        detail: entry.detail,
        status: entry.status,
        hosts: items.reduce((sum, s) => sum + (s.hostCount || 0), 0),
        sources: sourceLabels(items, row.name)
      };
    });

/** [{id, label, count}] for the "All" chip and one per source label, in label order. */
export const sourceFilters = projects => {
  const counts = new Map();
  projects.forEach(p => p.sources.forEach(source => counts.set(source, (counts.get(source) || 0) + 1)));
  return [
    { id: null, label: 'All', count: projects.length },
    ...[...counts].sort((a, b) => byName(a[0], b[0])).map(([id, count]) => ({ id, label: id, count }))
  ];
};

/**
 * A vendor's product rows narrowed by `query` (name, versions, sources or coverage) and sorted: `name`
 * A–Z, `hosts` most hosts first, `projects` most projects first; ties by name.
 */
export const filterProducts = (rows, { query, sort }) => {
  const q = (query || '').trim().toLowerCase();
  const by = {
    hosts: (a, b) => (b.hosts ?? -1) - (a.hosts ?? -1),
    projects: (a, b) => b.projects - a.projects
  }[sort];
  return rows
    .filter(
      p =>
        !q ||
        [p.name, p.versions, p.covered, ...p.sources]
          .join('\n')
          .toLowerCase()
          .includes(q)
    )
    .sort((a, b) => (by ? by(a, b) : 0) || byName(a.name, b.name));
};

/**
 * `projects` narrowed by `query` (name or source line) and `source`, sorted
 * by hosts (most first, then name) or by name (`sort` "name").
 */
export const filterProjects = (projects, { query, source, sort }) => {
  const q = (query || '').trim().toLowerCase();
  return projects
    .filter(p => !source || p.sources.includes(source))
    .filter(p => !q || `${p.name}\n${p.detail || ''}`.toLowerCase().includes(q))
    .sort((a, b) => (sort === 'name' ? 0 : b.hosts - a.hosts) || byName(a.name, b.name));
};
